import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ButtonBase, debounce, Stack, Typography } from '@mui/material';
import * as Ably from 'ably';
import { useChannel } from 'ably/react';

import { CfsApi, TActiveCFSFilter } from '@/api';
import { IGetParams } from '@/api/types';
import externalReadyToDispatchAudio from '@/assets/audios/external-ready-for-dispatch.mp3';
import newReadyToDispatchAudio from '@/assets/audios/new-ready-to-dispatch.mp3';
import { withLoader, WithLoaderProps } from '@/hocs';
import { useCFSContext } from '@/hooks';
import { AssignmentStatus, CFSEvent, ICFS } from '@/models';
import { playAudio } from '@/services';
import {
  ConfirmTypes,
  useAgencyStore,
  useToastStore,
  useUserStore,
} from '@/store';

import { CFSListItem } from '../components/CFSListItem';
import { ModalState } from '../shared/modalState';

const CfsListTab = ({
  showLoader,
  hideLoader,
  setActiveCfs,
  toggleModal,
  userCfs,
  userFilter,
  setFilteredCfs,
}: WithLoaderProps & {
  setActiveCfs: (cfs: ICFS) => void;
  toggleModal: (modalName: keyof ModalState) => void;
  userCfs: ICFS[];
  userFilter: TActiveCFSFilter;
  setFilteredCfs: (filtered: ICFS[]) => void;
}) => {
  const { updateToast } = useToastStore();
  const { updateCFS } = useCFSContext();
  const { account } = useUserStore();
  const navigate = useNavigate();
  const { agencies } = useAgencyStore();

  const [activeCFSList, setActiveCFSList] = useState<ICFS[] | null>(null);

  const isUserCfs = (cfs: ICFS) => {
    return userCfs.some((ucfs) => ucfs._id === cfs._id);
  };

  const isAssignedUserCfs = (cfs: ICFS) => {
    // Check if this CFS is the first item in userCfs array (assigned CFS)
    return userCfs.length > 0 && userCfs[0]._id === cfs._id;
  };

  const isQueuedUserCfs = (cfs: ICFS) => {
    // Check if this CFS is in userCfs array but not the first item (queued CFS)
    return (
      userCfs.length > 1 &&
      userCfs.slice(1).some((ucfs) => ucfs._id === cfs._id)
    );
  };

  useChannel(
    `account:${account?._id}:cfs:all`,
    debounce((message: Ably.Types.Message) => {
      if (message.name === CFSEvent.READY_TO_DISPATCH) {
        if (message.data.isExternal) {
          playAudio(externalReadyToDispatchAudio);
        } else {
          playAudio(newReadyToDispatchAudio);
        }
      }
      fetchActiveCFSList();
    }, 500),
  );

  useEffect(() => {
    fetchActiveCFSList();
  }, []);

  const handleClickedCFS = (cfs: ICFS) => {
    showLoader();
    if (!cfs._id) {
      return;
    }

    if (isUserCfs(cfs)) {
      navigate(`/my-cfs?cfsId=${cfs._id}`);
      return;
    }

    CfsApi.getOne(cfs._id)
      .then((response) => {
        setActiveCfs(response.data);
        updateCFS(response.data);
        toggleModal('cfsDetails');
        hideLoader();
      })
      .catch((err: any) => {
        updateToast({ open: true, message: err.message });
        hideLoader();
      });
  };

  const fetchActiveCFSList = () => {
    showLoader();
    const apiFilters: Record<string, string | unknown> = {};

    if (
      (userFilter?.assignmentStatus as string) === 'isReadyToDispatch' ||
      userFilter?.assignmentStatus === AssignmentStatus.NEW
    ) {
      apiFilters.assignmentStatus = AssignmentStatus.NEW;
    } else if (userFilter?.assignmentStatus) {
      apiFilters.assignmentStatus = userFilter?.assignmentStatus;
    }

    if (userFilter?.severity) {
      apiFilters.severity = userFilter?.severity;
    }

    if (userFilter?.readBy !== undefined) {
      apiFilters.readBy = userFilter?.readBy;
    }

    const params: IGetParams = {
      filter: JSON.stringify(apiFilters),
      sort: JSON.stringify({ createdAt: -1 }),
    };

    CfsApi.activeList(params)
      .then((response) => {
        let results = response.data;

        if ((userFilter?.assignmentStatus as string) === 'isReadyToDispatch') {
          results = results.filter((item) => item.isReadyToDispatch === true);
        } else if (userFilter?.assignmentStatus === AssignmentStatus.NEW) {
          results = results.filter((item) => item.isReadyToDispatch !== true);
        }

        if (userFilter?.agencies?.length) {
          results = results.filter((item) =>
            item.assignedAgencies?.some((assigned) =>
              userFilter.agencies?.includes(assigned),
            ),
          );
        }

        if (userFilter?.agencyType?.length) {
          const matchingAgencyIds = agencies
            .filter((agency) => userFilter?.agencyType?.includes(agency.type))
            .map((agency) => agency._id);

          results = results.filter((item) =>
            item.assignedAgencies?.some((agencyId) =>
              matchingAgencyIds.includes(agencyId),
            ),
          );
        }

        // Sort CFS records with assigned and queued at the top
        const sortedResults = [...results].sort((a, b) => {
          const aIsAssigned = isAssignedUserCfs(a);
          const aIsQueued = isQueuedUserCfs(a);
          const bIsAssigned = isAssignedUserCfs(b);
          const bIsQueued = isQueuedUserCfs(b);

          // Priority order: Assigned > Queued > Others
          if (aIsAssigned && !bIsAssigned) return -1;
          if (!aIsAssigned && bIsAssigned) return 1;

          if (aIsQueued && !bIsQueued && !bIsAssigned) return -1;
          if (!aIsQueued && bIsQueued && !aIsAssigned) return 1;

          // If both are in the same category, maintain original order
          return 0;
        });

        setActiveCFSList(sortedResults);
        setFilteredCfs(sortedResults);
      })
      .catch((err: any) => {
        updateToast({
          open: true,
          message: err.message || 'Failed to fetch active CFS list.',
          type: ConfirmTypes.ERROR,
        });
        setActiveCFSList([]);
      })
      .finally(() => {
        hideLoader();
      });
  };

  useEffect(() => {
    fetchActiveCFSList();
  }, [JSON.stringify(userFilter), JSON.stringify(userCfs)]);

  if (activeCFSList === null) {
    return (
      <Stack padding={3} gap={2}>
        <Typography>Loading...</Typography>
      </Stack>
    );
  }

  return (
    <Stack padding={3} gap={2}>
      {activeCFSList.length === 0 && <Typography>No active CFS</Typography>}
      {activeCFSList.map((cfs) => {
        const isAssigned = isAssignedUserCfs(cfs);
        const isQueued = isQueuedUserCfs(cfs);

        return (
          <ButtonBase
            onClick={() => handleClickedCFS(cfs)}
            key={cfs._id}
            sx={{
              display: 'flex',
              background: isAssigned ? '#5586FF20' : 'inherit',
              border: isAssigned ? '1px solid #5586FF' : 'none',
              borderRadius: 2,
            }}
          >
            <CFSListItem cfs={cfs} isQueued={isQueued}></CFSListItem>
          </ButtonBase>
        );
      })}
    </Stack>
  );
};

export default withLoader(CfsListTab);
